import type { ReactNode } from 'react';

// Per-condition safety guides — surfaced at /safety/:condition. Indexable
// content for organic search and shareable links from the phrasebook
// fallback chip when a language isn't QA'd. Each guide is a focused
// extension of the general SafetyScreen, written for one identity.

export interface ConditionGuideSection {
  title: string;
  body: ReactNode;
}

export interface ConditionGuide {
  slug: 'celiac' | 'halal' | 'kosher' | 'vegan';
  title: string;
  hero: string;
  sections: ConditionGuideSection[];
  finalWord: string;
}

export const CONDITION_GUIDES: Record<string, ConditionGuide> = {
  celiac: {
    slug: 'celiac',
    title: 'Celiac safety guide',
    hero: 'Celiac disease is autoimmune, not an allergy. A breadcrumb of cross-contamination triggers a real reaction. The app helps you communicate. Verification is on you.',
    sections: [
      {
        title: 'What to set in the app',
        body: (
          <>
            <p>
              Pick <strong>I have celiac disease</strong> in onboarding. The app auto-applies Wheat / Gluten as a hard filter and reasons your strict tags as celiac (so the source-language hand-off says "Tengo celiaquía" instead of "wheat allergy" — that distinction matters in countries where celiac is well understood and "wheat allergy" reads as fussy).
            </p>
            <p>
              Add <strong>No cross-contamination</strong> as a strict tag on every dish you tag. Shared fryers, shared boards, and shared pans are the real risk in non-celiac kitchens.
            </p>
            <p>
              Turn on <em>Also hide uncertain matches</em> in Settings. A "maybe contains wheat" dish is functionally a no.
            </p>
          </>
        ),
      },
      {
        title: 'Hidden gluten sources to ask about',
        body: (
          <ul style={{ paddingLeft: 18 }}>
            <li>Soy sauce (almost all wheat-fermented; only tamari is reliably gluten-free)</li>
            <li>Stock cubes, bouillons, gravies (often wheat-thickened)</li>
            <li>Fried foods sharing a fryer with breaded items</li>
            <li>Beer-batter on tempura, fried fish, onion rings</li>
            <li>Modified food starch (often wheat-derived in Europe)</li>
            <li>Communion wafers in religious-themed dishes</li>
            <li>Imitation crab and processed seafood</li>
          </ul>
        ),
      },
      {
        title: 'At the table',
        body: (
          <ol style={{ paddingLeft: 18 }}>
            <li>Show the strict celiac card. Ask the waiter to read it aloud.</li>
            <li>Ask explicitly: <em>"Will the kitchen prepare this on a clean surface, in a clean pan, with clean utensils?"</em></li>
            <li>If the waiter answers casually or says "you'll be fine," ask to speak with the chef or manager.</li>
            <li>If no one in the restaurant can confirm separate prep, leave. A second meal at a safer place beats a hospital trip.</li>
          </ol>
        ),
      },
    ],
    finalWord: 'Celiac is forever. The app is one layer. Pair it with verbal confirmation, your printed card, and your gut.',
  },

  halal: {
    slug: 'halal',
    title: 'Halal travel guide',
    hero: 'Halal in non-Muslim countries means navigating hidden pork, alcohol, and meat-source uncertainty. The app names the rules. The kitchen confirms the substance.',
    sections: [
      {
        title: 'What to set in the app',
        body: (
          <>
            <p>
              Pick <strong>I keep halal</strong> in onboarding. Pork and alcohol are auto-applied as hard filters. Your strict tags use halal-specific religious framing on the hand-off ("I keep halal — pork is forbidden by my faith"), not the generic medical phrasing.
            </p>
            <p>
              When you tag a dish strict, the consolidated halal card on the hand-off names pork-derived gelatin, lard, and broth explicitly, and asks the kitchen to confirm halal-slaughtered meat (zabihah) for any meat.
            </p>
            <p>
              Add <strong>No pork lard</strong> as a strict tag in Spanish, Mexican, Italian, and any cuisine where lard hides in beans, refried dishes, and pastry.
            </p>
          </>
        ),
      },
      {
        title: 'Hidden non-halal sources to ask about',
        body: (
          <ul style={{ paddingLeft: 18 }}>
            <li>Pork-based gelatin in desserts, marshmallows, gummy candies</li>
            <li>Wine reductions, beer batter, sake, mirin, cooking sherry</li>
            <li>Vanilla extract (alcohol-based — minor, but matters to some)</li>
            <li>Lard in baked goods, refried beans, tortillas, pastry crust</li>
            <li>Pancetta, prosciutto, or bacon as "garnish" not on the menu</li>
            <li>Pork-derived rennet in cheese</li>
            <li>Whey from non-halal-certified processing</li>
          </ul>
        ),
      },
      {
        title: 'On halal-slaughtered meat in non-Muslim countries',
        body: (
          <p>
            Halal-slaughtered meat (zabihah) is rare outside Muslim-majority countries. The app surfaces the request on every meat dish you order, but in most non-Muslim restaurants the answer will be "no, our meat isn't halal-certified." Plan to default to fish (with scales) or fully vegetarian dishes when no halal-certified restaurant is available.
          </p>
        ),
      },
    ],
    finalWord: 'The app names the rules in the kitchen\'s language. Your judgment is what makes it safe.',
  },

  kosher: {
    slug: 'kosher',
    title: 'Kosher travel guide',
    hero: 'Kosher abroad means three rules at once: no non-kosher animals, no meat-and-dairy mixing, and no shared prep. The app encodes all three. Confirmation is still on you.',
    sections: [
      {
        title: 'What to set in the app',
        body: (
          <>
            <p>
              Pick <strong>I keep kosher</strong> in onboarding. Pork and shellfish are auto-applied as hard filters. The kosher meal-mode engine activates: adding a meat dish to a dairy-mode cart (or vice versa) fires a contradiction guard.
            </p>
            <p>
              Catfish, eel, shark, swordfish, and other non-scaled fish are filtered out of "Kosher-friendly only" automatically — the app uses the fishKosher classification to enforce the scales-and-fins rule.
            </p>
            <p>
              The consolidated kosher card on the hand-off names pork, shellfish, kosher-certified meat, and meat-and-dairy separation in one paragraph the waiter reads once.
            </p>
          </>
        ),
      },
      {
        title: 'Hidden non-kosher sources to ask about',
        body: (
          <ul style={{ paddingLeft: 18 }}>
            <li>Non-scaled fish stock (from catfish, eel, monkfish)</li>
            <li>Shellfish stock in seemingly vegetarian soups</li>
            <li>Lard in pastry crust, refried beans, baked goods</li>
            <li>Non-kosher rennet in cheese (most non-Israeli cheese)</li>
            <li>Gelatin from non-kosher animals in desserts</li>
            <li>Wine that isn't certified kosher (matters for the strictest observance)</li>
            <li>Bread baked in pans greased with butter (renders the bread dairy)</li>
            <li>Dairy in dishes you'd assume are pareve (cream in pasta sauces, butter in fish)</li>
          </ul>
        ),
      },
      {
        title: 'On kosher-certified meat in non-Jewish countries',
        body: (
          <p>
            Kosher-certified meat is rare outside major Jewish communities. The app surfaces the request on every meat dish, but in most non-kosher restaurants you'll need to default to pareve (fish with scales+fins, or all-plant dishes). The app's "Kosher-friendly only" filter helps surface those quickly.
          </p>
        ),
      },
    ],
    finalWord: 'The app holds the rules so you don\'t have to negotiate them at every meal. Use the consolidated card. Trust your eyes too.',
  },

  vegan: {
    slug: 'vegan',
    title: 'Vegan travel guide',
    hero: 'Vegan abroad means hunting for hidden fish sauce, dairy, and animal-derived processing. The app surfaces the common landmines. The kitchen confirms the rest.',
    sections: [
      {
        title: 'What to set in the app',
        body: (
          <>
            <p>
              Pick <strong>I don\'t eat certain foods</strong> in onboarding, then tap <strong>Vegan</strong>, choose your reason (Ethical or preference), and continue. The "Vegan only" filter pill appears at the top of every menu — tap it to hide dishes containing animal products.
            </p>
            <p>
              For ethical reasons, the strict-vegan hand-off card uses honest framing — "I am strictly vegan" — instead of medical wording. Other reasons (medical, etc.) get appropriate framing automatically.
            </p>
            <p>
              In Southeast Asia and Japan, the cuisine-aware coach surfaces the fish sauce / dashi warning on first menu open. Tap <strong>No fish sauce</strong> as a strict tag on any dish you order — it explicitly names fish sauce, shrimp paste, and oyster sauce in the kitchen ask.
            </p>
          </>
        ),
      },
      {
        title: 'Hidden animal sources to ask about',
        body: (
          <ul style={{ paddingLeft: 18 }}>
            <li>Fish sauce, shrimp paste, oyster sauce (Southeast Asia)</li>
            <li>Dashi — fish or pork stock in Japanese soups, noodles, sauces</li>
            <li>Anchovy in Italian pasta sauces and Caesar dressing</li>
            <li>Chicken or beef stock cubes in "vegetable" soups</li>
            <li>Lard in Mexican refried beans, tortillas, baked goods</li>
            <li>Gelatin in desserts, marshmallows, gummies, panna cotta</li>
            <li>Non-vegan rennet in cheese, casein in "non-dairy" creamers</li>
            <li>Honey (depends on definition)</li>
            <li>Egg wash on pastries and bread</li>
          </ul>
        ),
      },
      {
        title: 'At the table',
        body: (
          <ol style={{ paddingLeft: 18 }}>
            <li>Use the "Vegan only" filter on the menu before you build the order — saves you scrolling.</li>
            <li>For dishes you tag, the strict-vegan card asks the kitchen to confirm.</li>
            <li>For Southeast Asian or Japanese cuisine, also tag No fish sauce strict — fish sauce won't show up in the dish description but it's almost certainly there.</li>
            <li>If the kitchen can't confirm, treat it as a maybe and skip.</li>
          </ol>
        ),
      },
    ],
    finalWord: 'The app does the work the menu won\'t do. Hidden animal ingredients are everywhere; your job is to ask, and the app makes asking easier.',
  },
};
